import { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  deselectNonCategoryColumns,
  returnColumnsToUnselectedState,
  selectHoveredColumns,
  selectColumns,
} from 'utils/charts/column';
import { flatten } from 'lodash/fp';
import chartLegendTitleSelector from 'selectors/chartLegendTitleSelector';
import genericChartCapsuleSelector from 'selectors/genericChartCapsuleSelector';
import useSelectorWithProps from 'hooks/useSelectorWithProps';
import ChartImpl from './ChartImpl';
import ChartLegend from './ChartLegend';

const propTypes = {
  blockId: PropTypes.string.isRequired,
  boardId: PropTypes.number.isRequired,
  perspectives: PropTypes.object,
  privacy: PropTypes.oneOf(['private', 'public']),
  scenarioId: PropTypes.number.isRequired,
  showLegend: PropTypes.bool,
  workspaceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};

const defaultProps = {
  perspectives: undefined,
  privacy: 'private',
  showLegend: false,
};

const GenericChart = ({
  blockId,
  boardId,
  perspectives,
  privacy,
  scenarioId,
  showLegend,
  workspaceId,
  ...restProps
}) => {
  const title = useSelectorWithProps(chartLegendTitleSelector, {
    blockId,
    public: privacy === 'public',
    workspaceId,
  });
  const { options, series, xUnit } = useSelectorWithProps(
    genericChartCapsuleSelector,
    {
      blockId,
      boardId,
      perspectives,
      public: privacy === 'public',
      scenarioId,
      workspaceId,
    },
  );

  const handleSelectPoints = useCallback((chart) => {
    deselectNonCategoryColumns(chart);
    selectHoveredColumns(chart);
  }, []);

  const handleSelectPointValue = useCallback((chart, value) => {
    if (value) {
      selectColumns(chart, value);
    } else {
      returnColumnsToUnselectedState(
        flatten(chart.series.map((s) => s.points)),
      );
    }
    chart.redraw();
  }, []);

  return (
    <ChartImpl
      formatLabel={xUnit.format}
      legend={
        showLegend ? (
          <ChartLegend icon='circle' series={series} title={title} />
        ) : undefined
      }
      onExternalClick={returnColumnsToUnselectedState}
      onSelectPoints={handleSelectPoints}
      onSelectPointValue={handleSelectPointValue}
      options={options}
      {...restProps}
    />
  );
};

GenericChart.propTypes = propTypes;
GenericChart.defaultProps = defaultProps;

export default GenericChart;
